// frontend/src/components/MobileDrawer.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-09-04
// Updated:      2026-09-16
//
// Description:
//               Mobile-only off-canvas application navigation for Vision Systems.
//
// Design:
//               • Consumes the same canonical visionNavigation definition as
//                 the desktop Sidebar.
//               • Occupies most of the viewport width without becoming part of
//                 the page document layout.
//               • Renders above a dimmed backdrop.
//               • Navigation area scrolls independently.
//               • Closes on navigation, backdrop click, or Escape.
//               • Provides a separate large Logout action at the bottom.
//               • Does not read or write the persisted desktop Sidebar state.
//
// Ownership:
//               Open/close state is owned by Layout.js. This component is a
//               presentation and interaction layer only.
//
// Readability refinement (2026-09-16):
//               • Navigation labels and icons are explicitly white on mobile.
//               • Active state remains visible through the existing row background
//                 and stronger font weight rather than changing foreground colour.
//               • Drawer behaviour, routing, logout and desktop Sidebar are unchanged.

import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaSignOutAlt, FaTimes } from "react-icons/fa";

import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import visionNavigation from "../navigation/visionNavigation";

const DRAWER_WIDTH = "min(86vw, 360px)";
const MOBILE_HEADER_HEIGHT = 64;
const MOBILE_NAV_FOREGROUND = "#ffffff";

const MobileDrawer = ({ open, onClose }) => {
  const { theme } = useTheme();
  const { logout } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && typeof onClose === "function") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) {
    return null;
  }

  const closeDrawer = () => {
    if (typeof onClose === "function") {
      onClose();
    }
  };

  const handleNavigate = (path) => {
    closeDrawer();
    navigate(path);
  };

  const handleLogout = () => {
    closeDrawer();
    logout();
  };

  const isActivePath = (path) => {
    if (path === "/dashboard") {
      return location.pathname === "/dashboard";
    }

    return (
      location.pathname === path ||
      location.pathname.startsWith(`${path}/`)
    );
  };

  const primaryColor =
    theme.primary_color || "var(--primary-color)" || "#1a1a1a";

  const accentColor =
    theme.accent_color || "var(--accent-color)" || "#d38a2e";

  const secondaryColor =
    theme.secondary_color || "var(--secondary-color)" || "#ffffff";

  return (
    <>
      <div
        role="presentation"
        aria-hidden="true"
        onClick={closeDrawer}
        style={{
          position: "fixed",
          top: `${MOBILE_HEADER_HEIGHT}px`,
          right: 0,
          bottom: 0,
          left: 0,

          backgroundColor: "rgba(0, 0, 0, 0.42)",

          zIndex: 1890,
        }}
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Application navigation"
        style={{
          position: "fixed",
          top: `${MOBILE_HEADER_HEIGHT}px`,
          right: 0,
          bottom: 0,

          width: DRAWER_WIDTH,
          maxWidth: "100%",

          display: "flex",
          flexDirection: "column",

          backgroundColor: primaryColor,
          color: secondaryColor,

          boxSizing: "border-box",

          zIndex: 1900,

          boxShadow: "-8px 0 24px rgba(0, 0, 0, 0.25)",

          overflow: "hidden",
        }}
      >
        <div
          style={{
            minHeight: "64px",
            flex: "0 0 auto",

            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",

            padding: "10px 14px 10px 20px",

            borderBottom: "1px solid rgba(255, 255, 255, 0.16)",

            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              fontSize: "1.15rem",
              fontWeight: 700,
              letterSpacing: "0.01em",
            }}
          >
            Menu
          </div>

          <button
            type="button"
            onClick={closeDrawer}
            aria-label="Close navigation menu"
            title="Close navigation menu"
            style={{
              width: "44px",
              height: "44px",

              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              padding: 0,

              border: "none",
              borderRadius: "8px",

              background: "transparent",
              color: accentColor,

              cursor: "pointer",
              touchAction: "manipulation",
            }}
          >
            <FaTimes size={25} />
          </button>
        </div>

        <nav
          aria-label="Mobile application navigation"
          style={{
            flex: "1 1 auto",
            minHeight: 0,

            overflowY: "auto",
            overflowX: "hidden",

            WebkitOverflowScrolling: "touch",
            overscrollBehaviorY: "contain",

            padding: "6px 0",

            boxSizing: "border-box",
          }}
        >
          {visionNavigation.map((item) => {
            const active = isActivePath(item.path);

            return (
              <button
                key={item.path}
                type="button"
                onClick={() => handleNavigate(item.path)}
                aria-current={active ? "page" : undefined}
                style={{
                  width: "100%",
                  minHeight: "58px",

                  display: "grid",
                  gridTemplateColumns: "48px minmax(0, 1fr)",
                  alignItems: "center",

                  gap: "8px",

                  padding: "0 18px",

                  border: "none",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.14)",

                  backgroundColor: active
                    ? "rgba(255, 255, 255, 0.10)"
                    : "transparent",

                  color: MOBILE_NAV_FOREGROUND,

                  textAlign: "left",

                  cursor: "pointer",
                  touchAction: "manipulation",

                  boxSizing: "border-box",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    minWidth: 0,

                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",

                    fontSize: "1.35rem",
                    color: MOBILE_NAV_FOREGROUND,
                  }}
                >
                  {item.icon}
                </span>

                <span
                  style={{
                    minWidth: 0,

                    fontSize: "1rem",
                    fontWeight: active ? 700 : 500,
                    color: MOBILE_NAV_FOREGROUND,

                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>

        <div
          style={{
            flex: "0 0 auto",

            padding: "14px 18px 18px",

            borderTop: "1px solid rgba(255, 255, 255, 0.16)",

            backgroundColor: primaryColor,

            boxSizing: "border-box",
          }}
        >
          <button
            type="button"
            onClick={handleLogout}
            style={{
              width: "100%",
              minHeight: "50px",

              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              gap: "10px",

              padding: "10px 16px",

              border: "none",
              borderRadius: "10px",

              backgroundColor: accentColor,
              color: primaryColor,

              fontSize: "1rem",
              fontWeight: 700,

              cursor: "pointer",
              touchAction: "manipulation",

              boxSizing: "border-box",
            }}
          >
            <FaSignOutAlt aria-hidden="true" size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default MobileDrawer;
